import React, { useState, useEffect, useRef, forwardRef } from 'react';
import propTypes from 'prop-types';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';

const block = 'c-sticky-hero-button';

export function StickyHeroButton(props) {
	const {
		label, 
		target, 
		href, 
		overlay, 
		disabled,
		dataAttributes,
		cssAnimation, 
		cssAnimationName,
		cssAnimationTrigger, 
		cssAnimationDuration,
		cssAnimationDelay, 
		hoverAnimation,
		onClickCallback,
		gtmTrackingCallback,
		id,
		icon,
		anchorRef,
		canAnchor,
	} = props;

	const getWindowDimensions = () => {
	  const { innerWidth: width, innerHeight: height } = window;
	  return {
	    width,
	    height
	  };
	};

	const [isDesktop, setIsDesktop] = useState(true);

	const useWindowDimensions = () => {
	  const [windowDimensions, setWindowDimensions] = useState(
	    getWindowDimensions()
	  );
	  useEffect(() => {
	    function handleResize() {
	      setWindowDimensions(getWindowDimensions());
	    }

	  	window.addEventListener("resize", handleResize);
	    return () => window.removeEventListener("resize", handleResize);
	  }, []);

	  useEffect(() => {
	  	setIsDesktop(windowDimensions.width > 992)
	  }, [windowDimensions])
	  return windowDimensions;
	};

	const getElementPosition = (element) => {
		if (document.elementsFromPoint) {
			let elements = document.elementsFromPoint(window.innerWidth/2, element.offsetTop + 30);

			let foundDarkTheme = false;
			elements.forEach((elt, i) => {
				if (elt.classList.contains('dark-theme') || elt.classList.contains('dark-indicator')) {
					foundDarkTheme = true;
				}
			});

			if (!elements.length || !initalised) {
				foundDarkTheme = true;
			}

			element.classList.toggle('light', foundDarkTheme);
		}
	}

	const Tag = href ? 'a' : 'button';
	const ref = useRef();

	const [scrollTop, setScrollTop] = useState(0);
	const [anchorPosition, setAnchorPosition] = useState(0);
	const [parentTop, setParentTop] = useState(0);
	const [initalised, setInitalised] = useState(false);

	const { height, width } = useWindowDimensions();
	const topOffset = height - 100;

	function clickHandler() {
		if (gtmTrackingCallback) {
			gtmTrackingCallback();
		}

		if (onClickCallback) {
			onClickCallback(props);
		}
	};

	useEffect(() => {
		setParentTop(ref.current.offsetParent.getBoundingClientRect().top)
		setAnchorPosition(anchorRef.current.getBoundingClientRect().top)
	
		if (isDesktop) {
			if (canAnchor) {
				ref.current.style.position = 'absolute'
				ref.current.style.top = anchorRef.current.getBoundingClientRect().top - ref.current.offsetParent.getBoundingClientRect().top - 62 + 'px'
			} else {
				ref.current.style.position = 'fixed'
				ref.current.style.top = topOffset - 62 + 'px'
			}
		} else {
			ref.current.style.position = 'static'
			ref.current.classList.toggle('light', false);
		}
	}, []);

	  useEffect(() => {
		if (scrollTop || initalised) {
			setInitalised(true)
			if (isDesktop) {
				if (scrollTop + topOffset > anchorPosition) {
					ref.current.style.top = topOffset - 62 + 'px'
					ref.current.style.position = 'fixed'
				} else if (canAnchor) {
					ref.current.style.position = 'absolute'
					ref.current.style.top = anchorPosition - parentTop - 62 + 'px'
				}
			} else {
				ref.current.style.position = 'static'
			}
		}

	    const onScroll = e => {
	      setScrollTop(e.target.documentElement.scrollTop);
	    };
	    window.addEventListener("scroll", onScroll);

	    if (isDesktop) {
		    getElementPosition(ref.current);
		}

	    return () => window.removeEventListener("scroll", onScroll);
	  }, [scrollTop]);

    return (
        <>
        	<div 
				id={id}
				ref={ref}
				className={`${block} light`}
			>
				<CssAnimation 
					enter={{name: cssAnimationName, target: 'element', duration: cssAnimationDuration, delay: `${cssAnimationDelay}, hidden`}} 
					triggerEnter={canAnchor ? parentTop > 0 : true}
					disable={!cssAnimation}
					key={label}
				>
					<div 
						className={`${block}__inner c-button__cta cmp-btn btn-secondary`}
					>
						<CssAnimation 
							enter={{name: 'fade-in', target: 'element', duration: 0.75, delay: `${cssAnimationDelay + 0.6}, hidden`}} 
							triggerEnter={canAnchor ? parentTop > 0 : true}
							disable={!cssAnimation}
						>
							<Tag
								target={target} 
								href={href} 
								data-overlay={overlay} 
								onClick={clickHandler}
								className={`${block}__button btn-text hover-animation`} 
								{...((Tag === 'button' && disabled) && { disabled: true })}
							>
								{label}
							</Tag>
						</CssAnimation>
					</div>
				</CssAnimation>
			</div>
        </> 
    );
}

StickyHeroButton.defaultProps = {
	label: 'MEET THE LBX',
	target: '_self',
	href: null,
	disabled: false,
	overlay: false,
	cssAnimation: true,
	cssAnimationTrigger: 'in-view-init',
	cssAnimationDuration: 1,
	cssAnimationDelay: 0,
	hoverAnimation: true,
	onClickCallback: () => { console.log('onClick callback') },
	brand: 'lexus',
	id: null,
	gtmName: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
	gtmComponentName: CORE_GTM_TRACKING_VALUES.componentName.button,
	gtmComponentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.campaigns,
	gtmAction: CORE_GTM_TRACKING_VALUES.action.clickCta,
	disableTracking: false,
	icon: false
};

StickyHeroButton.propTypes = {
	label: propTypes.string.isRequired,
	target: propTypes.string.isRequired,
	href: propTypes.string,
	overlay: propTypes.string,
	disabled: propTypes.bool,
	cssAnimation: propTypes.bool,
	cssAnimationName: propTypes.string,
	cssAnimationTrigger: propTypes.oneOfType([
		propTypes.string,
		propTypes.bool
	]),
	cssAnimationDuration: propTypes.number,
	cssAnimationDelay: propTypes.number,
	hoverAnimation: propTypes.bool,
	onClickCallback: propTypes.func,
	brand: propTypes.string.isRequired,
	id: propTypes.string,
	gtmName: propTypes.string,
	gtmComponentGroup: propTypes.string,
	gtmComponentName: propTypes.string,
	gtmLabel: propTypes.string,
	gtmAction: propTypes.string,
	disableTracking: propTypes.bool,
	icon: propTypes.oneOfType([
		propTypes.string,
		propTypes.bool
	])
};

export const StickyHeroButtonAnchor = forwardRef(function(props, ref) {
    return (
    	<div
	    	className={`${block}__anchor`}
	    	ref={ref}
	    >
    	</div>
    );
})